import { useCallback, useRef, useState } from "react";

import type { NewProjectOnMap } from "../ProjectListingMap";
import type { CreateProjectData } from "../createProject/types";
import { useProjectAreaPreview } from "../createProject/useProjectAreaPreview";
import { LOCATION_STEP, newCreateProjectData } from "../createProject/wizardSteps";
import type { FolderKey } from "./folderTree";

/** A new project's starting name, selected in its row so typing replaces it. */
export const NEW_PROJECT_NAME = "New Project";

type LatLng = { lat: number; lng: number };

export interface NewProjectFlowState {
  /** Changes for each new project, so its row starts fresh. */
  key: number;
  /** Named in its table row first, then set up step by step in the right-hand pane. */
  phase: "naming" | "steps";
  step: number;
  data: CreateProjectData;
}

const IDLE_DATA = newCreateProjectData();

/**
 * The listing page's new-project flow: a "New Project" row to name it, then
 * the create-project steps beside the listing map, which draws the project's
 * area and takes the location clicks. Nothing exists on the server until the
 * last step's Create.
 */
export function useNewProjectFlow() {
  const [flow, setFlow] = useState<NewProjectFlowState | null>(null);
  const seqRef = useRef(0);

  const start = useCallback((folderId: FolderKey, latlng: LatLng | null = null) => {
    seqRef.current += 1;
    setFlow({
      key: seqRef.current,
      phase: "naming",
      step: 0,
      data: newCreateProjectData({ name: NEW_PROJECT_NAME, folderId, selectedLatLng: latlng }),
    });
  }, []);
  const cancel = useCallback(() => setFlow(null), []);
  const updateData = useCallback(
    (updates: Partial<CreateProjectData>) => setFlow((f) => (f ? { ...f, data: { ...f.data, ...updates } } : f)),
    []
  );
  const commitName = useCallback(
    (name: string) => setFlow((f) => (f ? { ...f, phase: "steps", data: { ...f.data, name } } : f)),
    []
  );
  const setStep = useCallback((step: number) => setFlow((f) => (f ? { ...f, step } : f)), []);
  // A new spot moves the tile grid's origin, so tiles added around the old one go.
  const placeAt = useCallback(
    (latlng: LatLng) => updateData({ selectedLatLng: latlng, expansionTiles: [] }),
    [updateData]
  );

  const area = useProjectAreaPreview(flow?.data ?? IDLE_DATA, updateData);

  const onMap: NewProjectOnMap | null = flow
    ? {
        area,
        onPickLocation: flow.phase === "steps" && flow.step === LOCATION_STEP ? placeAt : undefined,
        hasLocation: !!flow.data.selectedLatLng,
      }
    : null;

  return { flow, start, cancel, commitName, updateData, setStep, placeAt, area, onMap };
}
